import { useCallback, useEffect, useRef, useState } from "react";
import api from "../api/endpoints";
import { API_URL, TOKEN_KEY, storage } from "../api/client";

/**
 * Drag-and-click map for choosing an exact point.
 *
 * Built on Google's Static Maps image with the pixel maths done here, matching
 * the mobile app so both clients behave identically and neither needs a Maps
 * JavaScript key in the browser bundle - the image is proxied by our server, so
 * the key stays server-side.
 *
 * Standard Web Mercator: at zoom z the world is 256 * 2^z pixels wide, so a
 * pixel offset from the centre converts to a coordinate offset exactly. That is
 * only true when the image is requested with an explicit centre and zoom rather
 * than letting Google auto-fit.
 */

const TILE = 256;
const worldSize = (zoom) => TILE * 2 ** zoom;

const project = ([lng, lat], zoom) => {
  const size = worldSize(zoom);
  const x = ((lng + 180) / 360) * size;
  const sin = Math.sin((lat * Math.PI) / 180);
  const y = (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)) * size;
  return { x, y };
};

const unproject = ({ x, y }, zoom) => {
  const size = worldSize(zoom);
  const lng = (x / size) * 360 - 180;
  const n = Math.PI * (1 - (2 * y) / size);
  const lat = (180 / Math.PI) * Math.atan(Math.sinh(n));
  return [lng, lat];
};

const shiftCenter = (center, zoom, dx, dy) => {
  const p = project(center, zoom);
  return unproject({ x: p.x + dx, y: p.y + dy }, zoom);
};

/** Reverse-geocodes a point into a readable address. */
export const addressFor = async (coordinates) => {
  try {
    const res = await api.maps.reverse({ lng: coordinates[0], lat: coordinates[1] });
    return res.data?.address || "";
  } catch (err) {
    return "";
  }
};

const MapPicker = ({ value, onChange, height = 300, zoom: initialZoom = 16 }) => {
  const [zoom, setZoom] = useState(initialZoom);
  const [center, setCenter] = useState(value || [77.209, 28.6139]);
  const [size, setSize] = useState({ width: 0, height });
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [drag, setDrag] = useState(null); // { startX, startY, dx, dy }

  const boxRef = useRef(null);
  const token = storage.get(TOKEN_KEY);

  // Track the element's real width so the requested image matches the box.
  useEffect(() => {
    const measure = () => {
      if (!boxRef.current) return;
      const r = boxRef.current.getBoundingClientRect();
      setSize({ width: r.width, height: r.height });
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  // Follow the parent when it sets a new point (e.g. a search result).
  useEffect(() => {
    if (!value) return;
    setCenter((cur) =>
      Math.abs(value[0] - cur[0]) > 1e-6 || Math.abs(value[1] - cur[1]) > 1e-6 ? value : cur
    );
  }, [value]);

  const commit = useCallback(
    (next) => {
      setCenter(next);
      if (onChange) onChange(next);
    },
    [onChange]
  );

  const pointFrom = (e) => {
    const t = e.touches?.[0] || e.changedTouches?.[0] || e;
    return { x: t.clientX, y: t.clientY };
  };

  const onDown = (e) => {
    const p = pointFrom(e);
    setDrag({ startX: p.x, startY: p.y, dx: 0, dy: 0 });
  };

  const onMove = (e) => {
    if (!drag) return;
    const p = pointFrom(e);
    // Live feedback - without this the map looks frozen until the mouse is up.
    setDrag((d) => (d ? { ...d, dx: p.x - d.startX, dy: p.y - d.startY } : d));
  };

  const onUp = (e) => {
    if (!drag) return;
    const { dx, dy } = drag;
    setDrag(null);

    // Barely moved: treat as a click and drop the pin where the cursor is.
    if (Math.abs(dx) < 5 && Math.abs(dy) < 5) {
      const box = boxRef.current?.getBoundingClientRect();
      if (!box) return;
      const p = pointFrom(e);
      commit(
        shiftCenter(center, zoom, p.x - box.left - box.width / 2, p.y - box.top - box.height / 2)
      );
      return;
    }
    // Dragged: the map moved with the cursor, so the centre moves opposite.
    commit(shiftCenter(center, zoom, -dx, -dy));
  };

  /**
   * No `pin` parameter: a Google-drawn marker is baked into the image, so it
   * lags a frame while dragging and doubles up with the overlay below.
   */
  const mapUrl =
    token && size.width
      ? `${API_URL}/maps/static?center=${center[0]},${center[1]}&zoom=${zoom}` +
        `&w=${Math.min(Math.round(size.width), 640)}&h=${Math.min(Math.round(size.height), 640)}` +
        `&token=${token}`
      : null;

  const changeZoom = (delta) => setZoom((z) => Math.min(Math.max(z + delta, 3), 20));

  return (
    <div
      ref={boxRef}
      className="mapPicker"
      style={{ height }}
      onMouseDown={onDown}
      onMouseMove={onMove}
      onMouseUp={onUp}
      onMouseLeave={() => setDrag(null)}
      onTouchStart={onDown}
      onTouchMove={onMove}
      onTouchEnd={onUp}
    >
      {mapUrl && !failed ? (
        <img
          key={mapUrl}
          src={mapUrl}
          alt="Map"
          className="mapPickerImg"
          draggable={false}
          style={{ transform: `translate(${drag?.dx || 0}px, ${drag?.dy || 0}px)` }}
          onLoad={() => setLoading(false)}
          onError={() => {
            setFailed(true);
            setLoading(false);
          }}
        />
      ) : (
        <div className="mapPickerFallback">
          <div style={{ fontSize: 30 }}>🗺️</div>
          <div>
            {failed
              ? "Map images are unavailable right now. Search for the address instead."
              : "Loading map…"}
          </div>
        </div>
      )}

      {/* One pin, anchored so its TIP is on the exact centre of the map. */}
      {!failed ? (
        <div className="mapPinAnchor">
          <span className={`mapPinShadow${drag ? " lifted" : ""}`} />
          <span className={`mapPin${drag ? " lifted" : ""}`} />
        </div>
      ) : null}

      {loading && !failed ? <div className="mapPickerSpinner" /> : null}

      {!failed ? (
        <>
          <div className="mapZoom">
            <button type="button" onClick={() => changeZoom(1)} aria-label="Zoom in">
              +
            </button>
            <button type="button" onClick={() => changeZoom(-1)} aria-label="Zoom out">
              −
            </button>
          </div>
          <div className="mapHint">Drag to move · click to drop the pin</div>
        </>
      ) : null}
    </div>
  );
};

export default MapPicker;
